import { Button as VFZButton } from '@vfz/ui';
import { Input as BaseInput } from 'components/Input/input.styles';
import styled, { css, desktop, rem } from 'theme';

interface PortalProps {
  fullWidth: boolean;
}

interface IframeProps {
  visible: boolean;
}

interface SubmitButtonProps {
  $active: boolean;
}

export const Portal = styled.div<PortalProps>`
  --conversational-ai-portal-offset: ${(p) => (p.fullWidth ? 0 : rem(44))};
  --conversational-ai-portal-top: ${(p) => (p.fullWidth ? 0 : 'auto')};
  --conversational-ai-portal-height: ${(p) =>
    p.fullWidth ? '100%' : `clamp(${rem(320)}, calc(100vh - var(--conversational-ai-portal-offset) * 2), ${rem(780)})`};
  --conversational-ai-portal-width: ${(p) =>
    p.fullWidth ? '100%' : `clamp(${rem(320)}, calc(100vh - var(--conversational-ai-portal-offset) * 2), ${rem(440)})`};
  --conversational-ai-portal-border: ${(p) => (p.fullWidth ? 0 : `${rem(1)} solid var(--ref-color-gray-30)`)};
  --conversational-ai-portal-border-radius: ${(p) => (p.fullWidth ? 0 : rem(20))};
  --conversational-ai-portal-box-shadow: ${(p) => (p.fullWidth ? 'none' : `0 0 ${rem(72)} 0 rgba(0, 0, 0, 0.35)`)};
  --conversational-ai-resume-offset: ${(p) => (p.fullWidth ? rem(11) : rem(44))};

  position: fixed;
  z-index: 3;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;

  > * {
    pointer-events: all;
  }
`;

export const ResumeButton = styled('button')`
  position: absolute;
  right: var(--conversational-ai-resume-offset);
  bottom: var(--conversational-ai-resume-offset);
  width: ${rem(60)};
  height: ${rem(60)};
  color: var(--ref-color-blackwhite-white);
  background: linear-gradient(90deg, #ff4608 0%, #f7931e 100%);
  border: 0;
  border-radius: 50%;
`;

export const Iframe = styled.iframe<IframeProps>`
  overflow-y: auto;
  position: absolute;
  top: var(--conversational-ai-portal-top);
  right: var(--conversational-ai-portal-offset);
  bottom: var(--conversational-ai-portal-offset);
  margin: 0 auto;
  width: var(--conversational-ai-portal-width);
  height: var(--conversational-ai-portal-height);
  border: var(--conversational-ai-portal-border);
  border-radius: var(--conversational-ai-portal-border-radius);
  box-shadow: var(--conversational-ai-portal-box-shadow);
  /* Smooth show/hide/minimize animation towards bottom-right corner */
  opacity: ${(p) => (p.visible ? 1 : 0)};
  transform-origin: bottom right;
  transform: ${(p) => (p.visible ? 'translate(0, 0) scale(1)' : 'translate(12px, 12px) scale(0.92)')};
  transition:
    opacity 200ms ease-out,
    transform 200ms ease-out;
  will-change: opacity, transform;
  pointer-events: ${(p) => (p.visible ? 'auto' : 'none')};
  background-color: var(--ref-color-gray-10);
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  top: var(--conversational-ai-portal-top);
  right: var(--conversational-ai-portal-offset);
  bottom: var(--conversational-ai-portal-offset);
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--conversational-ai-portal-width);
  height: var(--conversational-ai-portal-height);
  background-color: var(--ref-color-gray-10);
  border: var(--conversational-ai-portal-border);
  border-radius: var(--conversational-ai-portal-border-radius);
  box-shadow: var(--conversational-ai-portal-box-shadow);
  z-index: 1;
`;

export const Form = styled.form`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex-wrap: wrap;

  ${desktop(css`
    flex-wrap: nowrap;
  `)}
`;

// `Highlight` is a predefined system color, see https://www.w3.org/TR/CSS2/ui.html
export const Input = styled.div`
  --a11y-outline-color: Highlight;

  // Webkit focus styling
  @supports (outline-color: -webkit-focus-ring-color) {
    --a11y-outline-color: -webkit-focus-ring-color;
  }

  // Firefox Focus styling
  @supports (outline-color: -moz-mac-focusring) {
    --a11y-outline-color: -moz-mac-focusring;
  }

  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  align-self: stretch;
  justify-content: center;
  gap: ${rem(0)};
  padding: ${rem(16)};
  height: ${rem(52)};
  background-color: var(--ref-color-blackwhite-white);
  border: ${rem(1)} solid var(--ref-color-gray-40);
  border-radius: ${rem(12)};

  > input {
    padding: 0;
    color: var(--ref-color-gray-100);
    font-size: ${rem(16)};
    line-height: 1.6;
    font-weight: var(--ref-font-weight-300);

    &::placeholder {
      color: var(--ref-color-gray-70);
    }
  }
`;

export const InputField = styled(BaseInput).attrs({ error: false })`
  box-shadow: none;
  border: none;
  border-radius: 0;
  padding: ${rem(10)};
  width: 100%;
  height: 100%;

  &:focus-visible {
    outline: ${rem(2)} auto var(--a11y-outline-color);
    outline-offset: ${rem(4)};
  }
`;

const Button = styled(VFZButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(12)} ${rem(20)};
  min-width: ${rem(36)};
  color: var(--ref-color-gray-50);
  cursor: pointer;
  border: ${rem(1)} solid transparent;
  border-radius: ${rem(48)};
  transition:
    background-color 150ms ease-in-out,
    border-color 150ms ease-in-out;

  svg {
    width: ${rem(24)};
    height: ${rem(24)};
    vertical-align: bottom;
    fill: currentColor;
  }
`;

export const SubmitButton = styled(Button)<SubmitButtonProps>`
  background-color: var(--ref-color-gray-30);
  span {
    margin-left: ${rem(8)};
    font-weight: 700;
    color: var(--ref-color-gray-50);
  }

  &:hover,
  &:focus {
    background-color: var(--ref-color-gray-30);
    border-color: transparent;
    color: var(--ref-color-gray-50);
  }

  ${(p) =>
    p.$active &&
    css`
      background-color: var(--com-button-primary-color-default);
      color: var(--ref-color-blackwhite-white);

      span {
        color: var(--ref-color-blackwhite-white);
      }

      &:hover,
      &:focus {
        border-color: var(--com-button-primary-color-hover);
        background-color: var(--com-button-primary-color-hover);
        color: var(--ref-color-blackwhite-white);
      }
    `}

  /* Mobile/tablet: full-width button below input row */
  width: 100%;

  ${desktop(css`
    /* Desktop and bigger: inline button in the row */
    width: auto;
  `)}
`;

export const ButtonIcon = styled(Button)`
  background-color: var(--ref-color-blackwhite-white);
  border-color: currentColor;
  color: var(--com-button-primary-color-default);

  &:hover:enabled,
  &:focus:enabled {
    border-color: var(--com-button-primary-color-default);
    background-color: var(--com-button-primary-color-hover);
    color: var(--ref-color-blackwhite-white);
  }
  &.clear-button {
    border-color: var(--com-button-primary-color-default);
    background-color: var(--ref-color-blackwhite-white);
    width: ${rem(48)};
    padding: 0;
    height: ${rem(48)};
    border-radius: 50%;
  }

  svg {
    width: ${rem(20)};
    height: ${rem(20)};
  }
`;

export const FormHeader = styled.div`
  margin-bottom: ${rem(12)};
`;

export const FormTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(9)};
  color: var(--sys-color-secondary);
  margin-bottom: ${rem(16)};

  svg {
    display: none;
  }

  h2 {
    font-family: var(--ref-font-family-opensans);
    font-size: ${rem(24)};
    font-weight: var(--ref-font-weight-700);
    line-height: 1.1;
  }

  ${desktop(css`
    color: var(--sys-color-primary);

    svg {
      display: inline-flex;
    }
  `)}
`;

export const FormDescription = styled.p`
  font-size: ${rem(16)};
  font-weight: var(--ref-font-weight-300);
  margin: 0;

  strong {
    font-weight: var(--ref-font-weight-700);
  }
`;
